import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, HostListener, inject, input, Input, OnInit, PLATFORM_ID, Renderer2, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Subscription } from 'rxjs';
import { LG_BREAKPOINT } from '../../../../constants';
import { ILineItems } from '../../../../models/ILineItems';
import { IThankYouPageData } from '../../../../models/IThankYouPageData';
import { ITicket } from '../../../../models/ITicket';
import { ITicketsEventResponse } from '../../../../models/ITicketsEventResponse';
import { ApiService } from '../../../../services/api.service';
import { AuthService, LOGIN_TYPE_TICKETS, LOGIN_TYPE_TOPBAR } from '../../../../services/authentication/auth.service';
import { CashDonationService } from '../../../../services/donations/cash-donation.service';
import { EmbeddedPaymentService } from '../../../../services/epf/embedded-payment.service';
import { ModalService } from '../../../../services/modals/modal.service';
import { ThemeService } from '../../../../services/theme.service';
import { TicketService } from '../../../../services/tickets/ticket.service';
import { EmbeddedPaymentComponent } from '../../../embedded-payment/embedded-payment.component';
import { ZeroPurchaseThankyouPageComponent } from '../../../embedded-payment/zero-purchase-thankyou-page/zero-purchase-thankyou-page.component';
import { FooterComponent } from '../../footer/footer.component';
import { HeaderComponent } from '../../header/header.component';
import { TicketsComponent } from '../tickets.component';

@Component({
  selector: 'app-tickets-event',
  imports: [CommonModule,
    ButtonModule,
    TicketsComponent,
    CommonModule,
    InputTextModule,
    ButtonModule,
    FormsModule,
    FooterComponent,
    HeaderComponent,
    RouterModule,
    EmbeddedPaymentComponent,
    ProgressSpinnerModule,
    ZeroPurchaseThankyouPageComponent],
  templateUrl: './tickets-event.component.html',
  styleUrl: './tickets-event.component.scss'
})
export class TicketsEventComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private ticketService = inject(TicketService);
  private readonly donationService = inject(CashDonationService);
  private readonly paymentService = inject(EmbeddedPaymentService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly themeService = inject(ThemeService);
  authService = inject(AuthService);
  apiService = inject(ApiService);
  modalService = inject(ModalService);

  @Input() id!: number;

  public code = input('');

  tickets: ITicket[] = [];
  lineItems: ILineItems[] = [];
  ticketsSelectedAmount: ITicket[] = [];
  discountCode: string = '';
  comment!: string;
  appliedDiscountCode: string | null = null;
  discountApplied: boolean = false;
  discountInvalid: boolean = false;
  discountAvailable: boolean | null = null;
  hasTicketSelected: boolean = false;
  amounts!: number[];
  otherAmount: number | null = null;
  selectedAmount: number | null = null;
  totalAmount: number | null = null;
  donationAmount: number | null = null;
  totalPrice!: number;
  subtotal!: number;
  selectedTickets: ITicket[] = [];
  thankYouData!: IThankYouPageData;
  isZeroPurchase: boolean = false;
  errorMessage: string | null = null;



  isMobile: boolean = false;
  mobileStep = 1;

  //embedded payment
  token: string = '';
  visibleEmbedded: boolean = false;
  showThankYouPageModal: boolean = false;

  loadingEPF = this.paymentService.loadingEPF;

  ticketEventSignal = this.ticketService.ticketEventSignal;
  homePageSignal = this.apiService.homePageSignal;
  auctionConfigSignal = this.apiService.auctionConfigSignal;


  authSubscription!: Subscription;
  isAuthenticate: boolean = false;
  isLoading = this.ticketService.isLoading;

  protected showWinningBidText = computed(() => {
    return this.apiService.auctionConfigSignal()?.processor === 'pgf'
  });

  protected currency = computed(() => this.apiService.auctionConfigSignal()?.currency ?? '');


  isAuthenticated = computed(() => {
    return this.oidcSecurityService.authenticated().isAuthenticated;
  });


  protected isAskingForDonations: Signal<boolean> = computed(() => {
    let auctionConfigSignal = this.auctionConfigSignal();
    if (auctionConfigSignal?.isUsingCustomDonationLink) {
      return false;
    }
    let auctionTickets = this.ticketEventSignal();
    if (auctionTickets != null) {
      return auctionTickets.askForDonations;
    }
    return false;
  })

  protected isAnswerRequired: Signal<boolean> = computed(() => {
    let ticketEventSignal = this.ticketEventSignal();
    if (ticketEventSignal != null) {
      return ticketEventSignal.answerRequired;
    }
    return false;
  })

  private initialized = false;
  constructor(private router: Router, private renderer: Renderer2) {
    effect(() => {

      const data = this.ticketService.ticketEventSignal();
      if (!data) return;

      this.discountAvailable = data.isDiscountAvailable ?? null;
      const incoming = data.tickets;

      // preserve the number of tickets that the user had already selected
      //  sending quantitySelected values already initialized
      if (this.initialized) {
        const currentQuantities = new Map(this.tickets.map(t => [t.id, t.quantitySelected ?? 0]));
        this.tickets = incoming.map(t => ({
          ...t,
          quantitySelected: currentQuantities.get(t.id) ?? 0
        }));
      } else {
        // Initialize quantity selected first time
        this.tickets = incoming.map(t => ({
          ...t,
          quantitySelected: t.quantitySelected ?? 0
        }));
        this.initialized = true;
      }
      const dataDonations = this.donationService.cashDonationConfigSignal();
      if (dataDonations) {
        this.amounts = dataDonations.donationAmounts;
      }


    });
  }

  private unlistenMessage?: () => void; // for cleanup

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.themeService.customizeAuctionTheme(this.id);
    }

    if (typeof window !== 'undefined') {

      const discountCodeParam = this.code();
      const endpoint = 'tickets';
      if (discountCodeParam != undefined && discountCodeParam != null && discountCodeParam !== '') {
        this.discountCode = discountCodeParam;
        this.getTicketsDiscount(true);
      } else {
        this.getTicketsEvent(this.id, endpoint);
      }

      this.getCashDonationConfig();

      this.updateIsMobile();

      if (!this.apiService.homePageSignal()) {
        const endpoint = 'auctions';
        this.apiService.getAuctionHomePageData(endpoint, this.id!);
        localStorage.setItem('homePageData', JSON.stringify(this.apiService.homePageSignal()));
      }

      if (!this.auctionConfigSignal()) {
        this.apiService.getAuctionConfig(this.id).subscribe(config => {
          if (config?.ticketSalesMode === 'FS_TICKETS') {
            this.router.navigate(['/fs-tickets', this.id], { replaceUrl: true });
          }
        });
      } else if (this.auctionConfigSignal()?.ticketSalesMode === 'FS_TICKETS') {
        this.router.navigate(['/fs-tickets', this.id], { replaceUrl: true });
      }

      const ticketEventSignal = this.ticketService.ticketEventSignal();
      if (ticketEventSignal != null) {
        if (ticketEventSignal.comment != null) {
          this.comment = ticketEventSignal.comment
        }
      }

    }

    // Add window message listener for Unavailable Notification support
    this.unlistenMessage = this.renderer.listen('window', 'message', this.onHandleEpfMessage);

    // this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
    //   if (isAuth) {
    //     this.isAuthenticate = true
    //   } else {
    //     this.isAuthenticate = false
    //   }
    // });
  }

  ngOnDestroy() {
    // clean up the window listener if it was registered
    if (this.unlistenMessage) {
      this.unlistenMessage();
      this.unlistenMessage = undefined;
    }
  }

  onHandleEpfMessage = (event: any) => {
    console.log(event.data);

    if (event.data !== null) {
      if (event.data.type === "ITEM_UNAVAILABLE") {
        this.visibleEmbedded = false; // this closes modal
        window.location.reload();
        // If we want to route somewhere else, example:
        // this.route.navigate(['/itemunavailable']);
      } else if (event.data.type === "EPF_REDIRECT_PARENT_WITH_URL" && event.data.url) {
        console.log('Redirect to Thank You page');
        this.visibleEmbedded = false; // this closes modal
        window.location.replace(event.data.url);
      }

    }
  };


  resetSummary(): void {
    this.selectedTickets = [];
    this.ticketsSelectedAmount = [];
    this.donationAmount = 0;
    this.subtotal = 0;

    sessionStorage.removeItem('ticketSelectedAmount');
  }

  getTicketsEvent(auctionId: number, endpoint: string) {
    if (typeof window !== 'undefined') {

      const preSelected = this.ticketService.ticketEventSignal()?.tickets.filter(t => (t.quantitySelected ?? 0) > 0);
      // console.log(preSelected, 'preSelected')

      const selectedQuantities = new Map(
        preSelected?.filter(t => (t.quantitySelected ?? 0) > 0)
          .map(t => [t.id, t.quantitySelected || 0])
      )

      this.ticketService.getAllTickets(endpoint, auctionId).subscribe(({
        next: (response) => {

          this.tickets = response.tickets.map(ticket => ({
            ...ticket,
            price: ticket.price,
            discountedPrice: null,
            quantitySelected: selectedQuantities.get(ticket.id) || 0

          }));

          const selectedTickets = this.tickets.filter(t => (t.quantitySelected ?? 0) > 0);
          if (selectedTickets.length > 0) {
            this.updateSummary(selectedTickets);
          } else {
            this.resetSummary();
          }
        }, error: (error) => {
          // console.error('Error getting token:', error);
        }
      }));
    }
  }


  getCashDonationConfig() {
    if (typeof window !== 'undefined') {
      // console.log('CashDonation Executed')
      this.donationService.getDonationConfig(this.id).subscribe();
    }
  }


  updateDonationAmount(value: number | null, isOtherAmount: boolean = false) {
    if (isOtherAmount) {
      this.otherAmount = value;
      this.selectedAmount = null;
    } else {
      this.selectedAmount = (this.selectedAmount === value) ? null : value;
      this.otherAmount = null;
    }
    this.updateSummary(this.selectedTickets);
  }

  @HostListener('window:resize')
  onResize() {
    this.updateIsMobile();
  }

  private updateIsMobile() {
    this.isMobile = window.innerWidth < LG_BREAKPOINT;

    if (!this.isMobile) {
      this.mobileStep = 1;
    }
  }


  getTicketsDiscount(applyDiscount: boolean) {
    if (typeof window === 'undefined') return;
    if (applyDiscount && this.discountCode === '') return;

    const endpoint = 'tickets';

    // Save current selected quantities
    const selectedQuantities = new Map(this.selectedTickets.map(t => [t.id, t.quantitySelected || 0]));

    this.ticketService.getAllTickets(endpoint, this.id, this.discountCode).subscribe({
      next: (response) => {
        // console.log(JSON.stringify(response));
        this.discountApplied = response?.discountApplied;
        this.appliedDiscountCode = this.discountCode;
        this.discountCode = '';
        this.discountInvalid = applyDiscount && !this.discountApplied;

        // Update tickets with new prices and keep up quantities
        const updatedTickets = response?.tickets || [];
        this.tickets = updatedTickets.map(ticket => ({
          ...ticket,
          quantitySelected: selectedQuantities.get(ticket.id) || 0
        }));

        // update summary with tickets has quantity > 0
        this.updateSummary(this.tickets.filter(t => (t.quantitySelected || 0) > 0));
      },
      error: (error) => {
        console.error('Error applying discount:', error);
        this.discountApplied = false;
        this.appliedDiscountCode = null;
      }
    });
  }


  removeDiscount() {
    this.discountCode = '';
    this.discountApplied = false;
    this.discountInvalid = false;

    const endpoint = 'tickets';
    this.getTicketsDiscount(false);
  }

  goBack() {
    window.history.back();
  }

  // tickets on summary
  updateSummary(updatedTickets: ITicket[]): void {
    //  fill out the summary only quantity selected has value
    // const selected = updatedTickets.filter(ticket => (ticket.quantitySelected ?? 0) > 0);

    // // If there are no tickets, do not update anything
    // if (selected.length === 0) return;

    // Calculate the total price of tickets
    this.selectedTickets = updatedTickets;



    this.ticketsSelectedAmount = updatedTickets;
    const ticketsTotal = updatedTickets.reduce((sum, ticket) => {
      const price = parseFloat(String(ticket.price || '0').replace(/[^0-9.-]+/g, '')) || 0; //clean price $100.00
      return sum + price * (ticket.quantitySelected || 0);
    }, 0);

    // add donation
    this.donationAmount = Number(this.otherAmount ?? this.selectedAmount ?? 0);
    this.subtotal = ticketsTotal + this.donationAmount

    this.hasTicketSelected = this.ticketsSelectedAmount.some(ticket => (ticket.quantitySelected ?? 0) > 0);

    sessionStorage.setItem('ticketSelectedAmount', JSON.stringify(this.ticketsSelectedAmount));
  }

  showGuestInfo() {

    const showGuestForm = this.ticketsSelectedAmount.some(
      t => t.askForGuestInfo && t.quantitySelected! > 0
    );

    if (showGuestForm) {
      this.goToGuestForm();
    } else {
      if (this.isAuthenticated()) {
        this.embeddedPaymentLoad();
      } else {
        this.openLoginModal();
      }
    }

  }

  openLoginModal() {
    const isTestAuction = this.auctionConfigSignal()?.isTestAuction;
    if (this.hasTicketSelected && this.subtotal === 0) {
      this.authService.openLoginModal(LOGIN_TYPE_TOPBAR, this.embeddedPaymentLoad, 0, this.embeddedPaymentLoad);
    } else {
      this.authService.openLoginModal(
        LOGIN_TYPE_TICKETS,
        this.embeddedPaymentLoad,
        0,
        this.embeddedPaymentLoad,
        '',
        { hideGuestOption: isTestAuction }
      );
    }

  }

  embeddedPaymentLoad = (): void => {

    this.lineItems = this.ticketsSelectedAmount
      .filter(ticket => (ticket.quantitySelected ?? 0) > 0)
      .map(ticket => ({
        id: ticket.id,
        type: 4,
        quantity: ticket.quantitySelected ?? 0,
        donationAmount: 0.0
      }));

    // populate a donation
    if (this.donationAmount) {
      this.lineItems.push({
        id: this.id,
        type: 1,
        quantity: 1,
        donationAmount: this.donationAmount
      });
    }


    if (this.hasTicketSelected && this.subtotal === 0) {

      this.loadingEPF.set(true);

      this.paymentService
        .postEmbeddedPaymentSpecialPurchase(this.id!, this.lineItems, this.comment, this.appliedDiscountCode ?? '')
        .subscribe({
          next: (response: IThankYouPageData) => {
            this.loadingEPF.set(false);



            setTimeout(() => {
              this.thankYouData = response;
              this.showThankYouPageModal = true;
            },
              500);
          },
          error: (err: HttpErrorResponse) => {

            this.loadingEPF.set(false);


            if (err.status === 500) {
              return;
            } else {

              try {
                const parsed = JSON.parse(err.error);
                this.errorMessage = parsed.message;
              } catch {
                console.error('Error parsing error message');
              }
            }
          }

        });

    } else if (this.auctionConfigSignal()?.isTestAuction) {

      this.loadingEPF.set(true);

      this.paymentService
        .postTestPurchase(this.id!, this.lineItems, this.comment, undefined, this.appliedDiscountCode ?? '')
        .subscribe({
          next: (response: IThankYouPageData) => {
            this.loadingEPF.set(false);
            setTimeout(() => {
              this.thankYouData = response;
              this.showThankYouPageModal = true;
            }, 500);
          },
          error: (err: HttpErrorResponse) => {
            this.loadingEPF.set(false);
            if (err.status === 500) {
              return;
            } else {
              try {
                const parsed = JSON.parse(err.error);
                this.errorMessage = parsed.message;
              } catch {
                console.error('Error parsing error message');
              }
            }
          }
        });

    } else {

      this.loadingEPF.set(true);

      this.paymentService
        .postEmbeddedPayment(this.id!, this.lineItems, this.comment, undefined, this.appliedDiscountCode ?? '')
        .subscribe({
          next: (token) => {
            this.openEmbeddedPayment(token);
            this.loadingEPF.set(false);

          },
          error: (err: HttpErrorResponse) => {
            this.loadingEPF.set(false);


            if (err.status === 500) {
              return;
            } else {

              try {
                const parsed = JSON.parse(err.error);
                this.errorMessage = parsed.message;
              } catch {
                console.error('Error parsing error message');
              }
            }
          }
        });
    }
  }

  openEmbeddedPayment(token: string) {
    this.token = token;
    this.visibleEmbedded = true;
    this.updateSummary(this.ticketsSelectedAmount);
  }


  goToGuestForm() {
    if (this.hasTicketSelected && this.subtotal === 0) {
      this.isZeroPurchase = true;
    }

    const lineItems: ILineItems[] = this.ticketsSelectedAmount
      .filter(ticket => (ticket.quantitySelected ?? 0) > 0)
      .map(ticket => ({
        id: ticket.id,
        name: ticket.name,
        type: 4,
        quantity: ticket.quantitySelected ?? 0,
        donationAmount: 0.0
      }));

    // populate a donation
    if (this.donationAmount) {
      lineItems.push({
        id: this.id,
        type: 1,
        quantity: 1,
        donationAmount: this.donationAmount
      });
    }

    const enrichedTickets = this.ticketsSelectedAmount.map(ticket => ({
      ...ticket,
      quantitySelected: ticket.quantitySelected ?? 0
    }));

    const updatedEvent: ITicketsEventResponse = {
      tickets: enrichedTickets,
      meals: this.ticketService.ticketEventSignal()?.meals ?? [],
      discountCode: this.appliedDiscountCode || null,
      discountApplied: this.ticketService.ticketEventSignal()?.discountApplied ?? false,
      askForDonations: this.ticketService.ticketEventSignal()?.askForDonations ?? false,
      comment: this.comment ?? '',
      isZeroPurchase: this.isZeroPurchase,
      answerRequired: this.ticketService.ticketEventSignal()?.answerRequired ?? false
    };

    this.ticketService.ticketEventSignal.set(updatedEvent);

    this.router.navigate(['/guests-form', this.id], {
      state: { lineItems: lineItems },
    });

    // console.log(lineItems)
  }
}
